import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@finance/components/ui/dialog";

export function ApplyDialog({ row, onDone }) {
  const [open, setOpen] = useState(false);
  const [invs, setInvs] = useState([]);
  const [apps, setApps] = useState([]);
  const [f, setF] = useState({ invoice_id: "", amount: "" });
  const load = () => {
    api.get(`/advances/${row.id}/applicable-invoices`).then((r) => setInvs(r.data)).catch(() => {});
    api.get(`/advances/${row.id}/applications`).then((r) => setApps(r.data)).catch(() => {});
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = async () => {
    try { await api.post(`/advances/${row.id}/apply`, { invoice_id: f.invoice_id, amount: Number(f.amount) }); toast.success("Applied"); setF({ invoice_id: "", amount: "" }); load(); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reverse = async (a) => {
    const reason = window.prompt("Reason for reversing this application:"); if (!reason) return;
    try { await api.post(`/advances/applications/${a.id}/reverse`, { reason }); toast.success("Reversed"); load(); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (row.status === "Requested") return null;
  return (
    <>
      <Button size="sm" variant="outline" className="h-8" data-testid={`advance-apply-${row.ref_no}`} onClick={() => setOpen(true)}>Apply to invoice</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="advance-apply-dialog">
          <DialogHeader><DialogTitle className="font-display">{row.ref_no} \u00b7 open {row.currency} {fmtNum(row.open_balance)}</DialogTitle></DialogHeader>
          <select className="h-9 w-full rounded-sm border px-2 text-sm" data-testid="apply-invoice" value={f.invoice_id}
            onChange={(e) => { const i = invs.find((x) => x.id === e.target.value); setF({ invoice_id: e.target.value, amount: i ? Math.min(i.open, row.open_balance) : "" }); }}>
            <option value="">Select invoice\u2026</option>
            {invs.map((i) => <option key={i.id} value={i.id}>{i.no} \u00b7 {fmtDate(i.date)} \u00b7 open {i.currency} {fmtNum(i.open)}</option>)}
          </select>
          <div className="flex gap-2"><Input type="number" data-testid="apply-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
            <Button className="bg-[#1B4383]" data-testid="apply-confirm" disabled={!f.invoice_id} onClick={apply}>Apply</Button></div>
          <p className="text-[11px] text-gray-500">{row.type === "Prepayment" ? "Posts Dr 2000 AP / Cr 1450" : "Posts Dr 2110 / Cr 1100 AR"} \u2014 no P&L, no VAT. Partial allowed, over-application blocked.</p>
          <div className="lx-label mt-2">Applications</div>
          {apps.length === 0 && <div className="text-xs text-gray-400">None yet</div>}
          {apps.map((a) => (
            <div key={a.id} className="flex items-center justify-between border-t py-1 text-sm" data-testid={`apply-row-${a.id}`}>
              <span>{a.invoice_no} \u00b7 {fmtDate(a.date)} \u00b7 {fmtNum(a.amount)} {a.status === "reversed" && <span className="text-xs text-red-600">reversed: {a.reverse_reason}</span>}</span>
              {a.status === "applied" && <button className="text-xs text-red-600" data-testid={`apply-reverse-${a.id}`} onClick={() => reverse(a)}>Reverse</button>}
            </div>))}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SettleDialog({ row, banks, onDone, label }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ bank_account_id: "", amount: row.amount, fx_rate: "" });
  const go = async () => { try { await api.post(`/advances/${row.id}/settle-request`, { ...f, amount: Number(f.amount), fx_rate: f.fx_rate ? Number(f.fx_rate) : null }); toast.success("Posted"); setOpen(false); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <Button size="sm" className="h-8 bg-[#1B4383]" data-testid={`advance-settle-${row.ref_no}`} onClick={() => setOpen(true)}>{label}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="advance-settle-dialog">
          <DialogHeader><DialogTitle className="font-display">{label} \u00b7 {row.ref_no}</DialogTitle></DialogHeader>
          <select className="h-9 w-full rounded-sm border px-2 text-sm" data-testid="settle-bank" value={f.bank_account_id} onChange={(e) => setF({ ...f, bank_account_id: e.target.value })}>
            <option value="">Bank\u2026</option>{banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} ({b.currency})</option>)}</select>
          <Input type="number" data-testid="settle-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
          {row.currency !== "TZS" && <Input type="number" placeholder="FX rate" value={f.fx_rate} onChange={(e) => setF({ ...f, fx_rate: e.target.value })} />}
          <Button className="bg-[#1B4383]" data-testid="settle-confirm" onClick={go}>Post</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function TieoutStrip({ type }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/advances-tieout").then((r) => setD(r.data[type])).catch(() => {}); }, [type]);
  if (!d) return null;
  return (
    <div className={`mb-3 rounded-sm border px-3 py-2 text-xs ${d.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid={`tieout-${d.account}`}>
      {d.ok ? "\u2713" : "\u26a0"} GL {d.account}: TZS {fmtNum(d.gl)} \u00b7 Register open: TZS {fmtNum(d.register)}{!d.ok && ` \u00b7 Difference TZS ${fmtNum(d.difference)}`}
    </div>
  );
}

export function RequestAdvanceButton({ type, partyId, poId, poNo, currency, defaultAmount, testid }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: defaultAmount || "", pfi_ref: "" });
  const sup = type === "Prepayment";
  const go = async () => {
    try { await api.post("/advances/requests", { type, party_id: partyId, po_id: poId, po_no: poNo, currency, amount: Number(f.amount), pfi_ref: f.pfi_ref }); toast.success(sup ? "Advance requested \u2014 shows in Payables Due" : "Deposit requested"); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!partyId) return null;
  return (
    <>
      <Button size="sm" variant="outline" data-testid={testid} onClick={() => setOpen(true)}>{sup ? "Request Advance" : "Request Deposit"}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="request-advance-dialog">
          <DialogHeader><DialogTitle className="font-display">{sup ? "Request supplier advance" : "Request client deposit"} \u00b7 {poNo}</DialogTitle></DialogHeader>
          <label className="text-xs">Amount ({currency})<Input type="number" className="mt-1" data-testid="request-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></label>
          {sup && <label className="text-xs">Supplier PFI reference *<Input className="mt-1" data-testid="request-pfi" value={f.pfi_ref} onChange={(e) => setF({ ...f, pfi_ref: e.target.value })} /></label>}
          <Button className="bg-[#1B4383]" data-testid="request-confirm" onClick={go}>Send request</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function DepositRequestsPanel() {
  const [rows, setRows] = useState([]);
  const [banks, setBanks] = useState([]);
  const load = () => api.get("/advances?type=Prepayment&status=Requested").then((r) => setRows(r.data || []));
  useEffect(() => { load(); api.get("/bank-accounts").then((r) => setBanks(r.data || [])); }, []);
  if (!rows.length) return null;
  return (
    <div className="mb-4 rounded-sm border border-amber-200 bg-amber-50/40 p-3" data-testid="deposit-requests-panel">
      <div className="lx-label mb-1">Prepayment requests ({rows.length})</div>
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between border-t border-amber-100 py-1.5 text-sm">
          <span><span className="mr-1 rounded-sm bg-amber-200 px-1 text-[10px] font-bold">REQUEST</span>{r.ref_no} \u00b7 {r.party_name} \u00b7 {(r.allocations || []).map((a) => a.po_no).join(", ")} \u00b7 PFI {r.pfi_ref || "\u2014"} \u00b7 <b>{r.currency} {fmtNum(r.amount)}</b></span>
          <SettleDialog row={r} banks={banks} onDone={load} label="Pay deposit" />
        </div>))}
    </div>
  );
}
